import { useState } from 'react';

export interface BannerFormData {
  htmlContent: string;
  clickActionType: 'html_embed' | 'external_url' | 'service_link';
  clickActionUrl: string;
  displayOrder: number;
  isActive: boolean;
}

interface Banner {
  id: string;
  // Support both camelCase (from API response) and snake_case (legacy)
  html_content?: string;
  htmlContent?: string;
  click_action_type?: 'html_embed' | 'external_url' | 'service_link';
  clickActionType?: 'html_embed' | 'external_url' | 'service_link';
  click_action_url?: string | null;
  clickActionUrl?: string | null;
  display_order?: number;
  displayOrder?: number;
  is_active?: boolean;
  isActive?: boolean;
  is_shared?: boolean;
  isShared?: boolean;
}

interface BannerEditorProps {
  banners: Banner[];
  companyId: string | null;
  isShared: boolean;
  onSave: (data: BannerFormData, id?: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  loading?: boolean;
}

const emptyForm: BannerFormData = {
  htmlContent: '',
  clickActionType: 'external_url',
  clickActionUrl: '',
  displayOrder: 1,
  isActive: true,
};

const clickActionTypeLabels: Record<string, string> = {
  html_embed: 'HTML Embed',
  external_url: 'External URL',
  service_link: 'Service Link',
};

export function BannerEditor({ banners, companyId, isShared, onSave, onDelete, loading }: BannerEditorProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<BannerFormData>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  function validate(data: BannerFormData): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!data.htmlContent.trim()) {
      errs.htmlContent = 'HTML content is required';
    }
    if (!Number.isInteger(data.displayOrder) || data.displayOrder < 1) {
      errs.displayOrder = 'Display order must be a positive integer';
    }
    return errs;
  }

  function getBannerField<T>(banner: Banner, camel: keyof Banner, snake: keyof Banner, fallback: T): T {
    const val = banner[camel] ?? banner[snake];
    return (val !== undefined && val !== null ? val : fallback) as T;
  }

  function handleEdit(banner: Banner) {
    setEditingId(banner.id);
    setFormData({
      htmlContent: getBannerField(banner, 'htmlContent', 'html_content', ''),
      clickActionType: getBannerField(banner, 'clickActionType', 'click_action_type', 'external_url'),
      clickActionUrl: getBannerField(banner, 'clickActionUrl', 'click_action_url', '') ?? '',
      displayOrder: getBannerField(banner, 'displayOrder', 'display_order', 1),
      isActive: getBannerField(banner, 'isActive', 'is_active', true),
    });
    setErrors({});
    setShowForm(true);
  }

  function handleNew() {
    setEditingId(null);
    const nextOrder = banners.length > 0 ? Math.max(...banners.map((b) => getBannerField(b, 'displayOrder', 'display_order', 1))) + 1 : 1;
    setFormData({ ...emptyForm, displayOrder: nextOrder });
    setErrors({});
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setEditingId(null);
    setFormData(emptyForm);
    setErrors({});
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSaving(true);
    try {
      await onSave(formData, editingId ?? undefined);
      handleCancel();
    } finally {
      setSaving(false);
    }
  }

  function handleFieldChange(field: keyof BannerFormData, value: string | number | boolean) {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  }

  const sortedBanners = [...banners].sort((a, b) => getBannerField(a, 'displayOrder', 'display_order', 1) - getBannerField(b, 'displayOrder', 'display_order', 1));

  return (
    <div className="banner-editor">
      <div className="banner-editor__header">
        <h3>{isShared ? 'Shared Banners' : 'Company Banners'}</h3>
        <button className="btn btn-primary" onClick={handleNew} disabled={loading || saving}>
          + Add Banner
        </button>
      </div>

      {showForm && (
        <form className="banner-editor__form" onSubmit={handleSubmit} aria-label="Banner form">
          <div className="form-group">
            <label htmlFor="banner-html-content">HTML Content *</label>
            <textarea
              id="banner-html-content"
              value={formData.htmlContent}
              onChange={(e) => handleFieldChange('htmlContent', e.target.value)}
              placeholder="Enter banner HTML content"
              rows={6}
              aria-invalid={!!errors.htmlContent}
              aria-describedby={errors.htmlContent ? 'banner-html-error' : undefined}
            />
            {errors.htmlContent && (
              <span id="banner-html-error" className="form-error">{errors.htmlContent}</span>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="banner-click-action-type">Click Action Type *</label>
              <select
                id="banner-click-action-type"
                value={formData.clickActionType}
                onChange={(e) => handleFieldChange('clickActionType', e.target.value)}
              >
                {Object.entries(clickActionTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="banner-display-order">Display Order *</label>
              <input
                id="banner-display-order"
                type="number"
                min="1"
                value={formData.displayOrder}
                onChange={(e) => handleFieldChange('displayOrder', parseInt(e.target.value, 10) || 0)}
                aria-invalid={!!errors.displayOrder}
                aria-describedby={errors.displayOrder ? 'banner-order-error' : undefined}
              />
              {errors.displayOrder && (
                <span id="banner-order-error" className="form-error">{errors.displayOrder}</span>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="banner-click-action-url">Click Action URL</label>
            <input
              id="banner-click-action-url"
              type="text"
              value={formData.clickActionUrl}
              onChange={(e) => handleFieldChange('clickActionUrl', e.target.value)}
              placeholder="URL or HTML content for click action"
            />
          </div>

          <div className="form-group">
            <label htmlFor="banner-is-active" className="toggle-label">
              <input
                id="banner-is-active"
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => handleFieldChange('isActive', e.target.checked)}
              />
              <span>Active</span>
            </label>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update Banner' : 'Create Banner'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="loading-text">Loading banners...</p>
      ) : sortedBanners.length === 0 ? (
        <p className="empty-text">No banners configured yet.</p>
      ) : (
        <table className="data-table" aria-label="Banners list">
          <thead>
            <tr>
              <th>Order</th>
              <th>Action Type</th>
              <th>Active</th>
              <th>HTML Preview</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedBanners.map((banner) => (
              <tr key={banner.id}>
                <td>{getBannerField(banner, 'displayOrder', 'display_order', 1)}</td>
                <td>{clickActionTypeLabels[getBannerField(banner, 'clickActionType', 'click_action_type', 'external_url')]}</td>
                <td>{getBannerField(banner, 'isActive', 'is_active', true) ? '✓' : '✗'}</td>
                <td className="description-cell">
                  {(() => { const html = getBannerField(banner, 'htmlContent', 'html_content', ''); return html.length > 80 ? `${html.slice(0, 80)}...` : html; })()}
                </td>
                <td className="actions-cell">
                  <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(banner)}>
                    Edit
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => onDelete(banner.id)}
                    aria-label={`Delete banner ${banner.id}`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
